import { useEffect, useMemo, useRef, useState } from "react";

import {
  RESOURCES,
  RESOURCE_TYPE_LABELS,
  Resource,
  ResourceCategory,
  ResourceType,
} from "src/sections/get-started/resources";
import "src/sections/get-started/resources-drawer.css";

type SortMode = "featured" | "alpha";

const TYPE_ICONS: Record<ResourceType, JSX.Element> = {
  video: (
    <svg viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
      <path d="M5.5 4.2a.6.6 0 0 1 .9-.52l6 3.8a.6.6 0 0 1 0 1.04l-6 3.8a.6.6 0 0 1-.9-.52V4.2Z" />
    </svg>
  ),
  article: (
    <svg
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.4"
      strokeLinecap="round"
      aria-hidden="true"
    >
      <path d="M4 3.5h8M4 6.5h8M4 9.5h8M4 12.5h5" />
    </svg>
  ),
  guide: (
    <svg
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.4"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M8 3.5c-1.2-1-2.8-1.2-4.5-1v10c1.7-.2 3.3 0 4.5 1 1.2-1 2.8-1.2 4.5-1v-10c-1.7-.2-3.3 0-4.5 1Zm0 0v10" />
    </svg>
  ),
  form: (
    <svg
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.4"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <rect x="3" y="2.5" width="10" height="11" rx="1.5" />
      <path d="M5.5 5.5h2M5.5 8h5M5.5 10.5h5" />
    </svg>
  ),
  template: (
    <svg
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.4"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <rect x="2.5" y="2.5" width="11" height="11" rx="1.5" />
      <path d="M2.5 6h11M6 6v7.5" />
    </svg>
  ),
  call: (
    <svg
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.4"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M13.5 8A5.5 5.5 0 1 0 8 13.5H13a.5.5 0 0 0 .5-.5V8Z" />
      <path d="M5.5 7h5M5.5 9.5h3" />
    </svg>
  ),
};

interface ResourcesDrawerProps {
  open: boolean;
  onClose: () => void;
}

export function ResourcesDrawer({ open, onClose }: ResourcesDrawerProps) {
  const [typeFilter, setTypeFilter] = useState<ResourceType | null>(null);
  const [categoryFilter, setCategoryFilter] = useState<ResourceCategory | null>(
    null,
  );
  const [sortMode, setSortMode] = useState<SortMode>("featured");
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const lastFocusedRef = useRef<Element | null>(null);

  const types = useMemo(
    () => Array.from(new Set(RESOURCES.map((r) => r.type))) as ResourceType[],
    [],
  );
  const categories = useMemo(
    () =>
      Array.from(
        new Set(RESOURCES.map((r) => r.category)),
      ) as ResourceCategory[],
    [],
  );

  const visible = useMemo(() => {
    let list: Resource[] = RESOURCES.filter(
      (r) =>
        (!typeFilter || r.type === typeFilter) &&
        (!categoryFilter || r.category === categoryFilter),
    );
    if (sortMode === "alpha") {
      list = [...list].sort((a, b) => a.title.localeCompare(b.title));
    }
    return list;
  }, [typeFilter, categoryFilter, sortMode]);

  useEffect(() => {
    if (!open) return;
    lastFocusedRef.current = document.activeElement;
    closeButtonRef.current?.focus();
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.body.style.overflow = prevOverflow;
      document.removeEventListener("keydown", onKeyDown);
      if (lastFocusedRef.current instanceof HTMLElement) {
        lastFocusedRef.current.focus();
      }
    };
  }, [open, onClose]);

  const clearFilters = () => {
    setTypeFilter(null);
    setCategoryFilter(null);
  };

  return (
    <div className={`sp-res-root${open ? " is-open" : ""}`} aria-hidden={!open}>
      <div className="sp-res-overlay" onClick={onClose} />
      <aside
        className="sp-res-panel"
        role="dialog"
        aria-modal="true"
        aria-label="Resources"
      >
        <div className="sp-res-filters">
          <select
            className="sp-res-select"
            aria-label="Filter by type"
            value={typeFilter ?? ""}
            onChange={(e) =>
              setTypeFilter((e.target.value || null) as ResourceType | null)
            }
          >
            <option value="">All types</option>
            {types.map((t) => (
              <option key={t} value={t}>
                {RESOURCE_TYPE_LABELS[t]}
              </option>
            ))}
          </select>
          <select
            className="sp-res-select"
            aria-label="Filter by category"
            value={categoryFilter ?? ""}
            onChange={(e) =>
              setCategoryFilter(
                (e.target.value || null) as ResourceCategory | null,
              )
            }
          >
            <option value="">All categories</option>
            {categories.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
          <button
            ref={closeButtonRef}
            type="button"
            className="sp-res-close"
            onClick={onClose}
            aria-label="Close resources"
          >
            <svg
              viewBox="0 0 16 16"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.6"
              strokeLinecap="round"
              aria-hidden="true"
            >
              <path d="M4 4l8 8M12 4l-8 8" />
            </svg>
          </button>
        </div>

        <div className="sp-res-toolbar">
          <span className="sp-res-count">
            {visible.length} {visible.length === 1 ? "resource" : "resources"}
          </span>
          <div className="sp-res-sort" role="group" aria-label="Sort resources">
            <button
              type="button"
              className={`sp-res-sort-option${sortMode === "featured" ? " is-active" : ""}`}
              onClick={() => setSortMode("featured")}
            >
              Featured
            </button>
            <button
              type="button"
              className={`sp-res-sort-option${sortMode === "alpha" ? " is-active" : ""}`}
              onClick={() => setSortMode("alpha")}
            >
              A–Z
            </button>
          </div>
        </div>

        <div className="sp-res-list">
          {visible.map((r) => (
            <a
              key={r.id}
              className="sp-res-item"
              href={r.url}
              target="_blank"
              rel="noopener noreferrer"
            >
              <span className="sp-res-item-icon" data-type={r.type}>
                {TYPE_ICONS[r.type]}
              </span>
              <span className="sp-res-item-body">
                <span className="sp-res-item-title">{r.title}</span>
                <span className="sp-res-item-desc">{r.description}</span>
                <span className="sp-res-item-meta">
                  {RESOURCE_TYPE_LABELS[r.type]} · {r.category}
                  {r.duration ? ` · ${r.duration}` : ""}
                </span>
              </span>
              <svg
                className="sp-res-item-arrow"
                viewBox="0 0 16 16"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <path d="M5 11l6-6M6 5h5v5" />
              </svg>
            </a>
          ))}
          {visible.length === 0 && (
            <div className="sp-res-empty">
              <p>Nothing matches those filters.</p>
              <button
                type="button"
                className="sp-res-clear"
                onClick={clearFilters}
              >
                Clear filters
              </button>
            </div>
          )}
        </div>
      </aside>
    </div>
  );
}
